import React from 'react';

import { Person, Schedule, Start } from '@mui/icons-material';
import type { DrawerProps } from '@mui/material';
import {
  Avatar,
  AvatarGroup,
  CardContent,
  Drawer,
  IconButton,
  InputAdornment,
  Stack,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { clamp, every, forEach, get, isEmpty, isNil, map, size, uniq } from 'lodash';
import { Controller } from 'react-hook-form';
import { CardLevel, ContainerFlexHeight, LoadingButton, NumberTextField } from '../../../../components';
import TimePickerWithShorthand from '../../../../components/time_picker_with_shorthand';
import { createScopedI18n } from '../../../../i18n/i18n';
import { calculateDurationMinute, formatTime, joinPairs, syncTimeToDate } from '../../../../utils/libs';
import snackbar, { SAVE_SUCCESS } from '../../../../utils/snackbar';
import { AttendancesPageContext } from '../attendances_page_context';
import useApproveAttendancesMutation from '../hooks/use_approve_attendances_mutation';
import useAttendanceEditForm from '../hooks/use_attendance_edit_form';
import { isAttendanceInRange } from '../hooks/use_attendance_location';
import useRejectAttendancesMutation from '../hooks/use_reject_attendances_mutation';
import useUpdateAttendancesMutation from '../hooks/use_update_attendances_mutation';

import type { AttendanceEditFormTransformedValues } from '../hooks/use_attendance_edit_form';
import type { JobHiringAttendance } from '../type';

const attendancesDetailPageI18n = createScopedI18n('pages.attendances_id');

export type BatchDrawerProps = Omit<DrawerProps, 'children' | 'open'> & {
  open?: boolean;
};

type BatchDrawerInnerProps = BatchDrawerProps & {
  open: boolean;
  setOpenBatchModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedAttendances: JobHiringAttendance[];
  selectAttendance: (attendanceId: string, selected: boolean) => void;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
};

const BatchDrawer = React.memo(
  ({
    open,
    setOpenBatchModal,
    selectedAttendances,
    selectAttendance,
    setHasUnsavedChanges,
    ...props
  }: BatchDrawerInnerProps) => {
    const theme = useTheme();

    const onCloseHandler = React.useCallback(() => {
      setOpenBatchModal(false);
    }, [setOpenBatchModal]);

    const avatarUrls = React.useMemo(
      () => map(selectedAttendances, (attendance) => attendance.jobHiring?.student?.avatar ?? null),
      [selectedAttendances],
    );

    const defaultFormValues = React.useMemo(() => {
      const values: {
        startOperationTime: string | null;
        endOperationTime: string | null;
        breakDurationMinute: number | null;
      } = {
        startOperationTime: null,
        endOperationTime: null,
        breakDurationMinute: null,
      };

      if (isEmpty(selectedAttendances)) return values;

      // Only set default values if all values are the same, otherwise set to null
      const startTimes = uniq(map(selectedAttendances, (attendance) => attendance.startOperationTime));
      const endTimes = uniq(map(selectedAttendances, (attendance) => attendance.endOperationTime));
      const breakDurations = uniq(map(selectedAttendances, (attendance) => attendance.breakDurationMinute));

      values.startOperationTime = size(startTimes) === 1 ? startTimes[0] : null;
      values.endOperationTime = size(endTimes) === 1 ? endTimes[0] : null;
      values.breakDurationMinute = size(breakDurations) === 1 ? breakDurations[0] : null;

      return values;
    }, [selectedAttendances]);

    const {
      control,
      handleSubmit,
      reset,
      watch,
      getValues,
      setValue,
      formState: { isDirty },
    } = useAttendanceEditForm({ defaultValues: defaultFormValues });

    const job = React.useMemo(() => selectedAttendances[0]?.jobHiring?.job, [selectedAttendances]);
    const editable = React.useMemo(() => {
      if (isEmpty(selectedAttendances)) return false;
      return every(selectedAttendances, (attendance) => attendance.status === 'pending');
    }, [selectedAttendances]);

    const jobOtHour = job?.otHour ?? null;
    const jobDurationMinutes = React.useMemo(() => {
      if (isNil(job?.jobDurationMinute)) {
        return calculateDurationMinute(job?.jobStartAt, job?.jobEndAt, job?.breakDurationMinute);
      }
      return job?.jobDurationMinute ?? null;
    }, [job?.jobDurationMinute, job?.jobStartAt, job?.jobEndAt, job?.breakDurationMinute]);
    const jobDurationHours = isNil(jobDurationMinutes) ? null : jobDurationMinutes / 60.0;

    const startOperationTime = watch('startOperationTime');
    const endOperationTime = watch('endOperationTime');
    const breakDurationMinute = watch('breakDurationMinute');

    const operationDurationMinutes = React.useMemo(
      () => calculateDurationMinute(startOperationTime, endOperationTime, breakDurationMinute),
      [startOperationTime, endOperationTime, breakDurationMinute],
    );
    const operationDurationHours = isNil(operationDurationMinutes) ? null : operationDurationMinutes / 60.0;
    const operationDurationOtHours = React.useMemo(() => {
      if (isNil(jobOtHour) || isNil(operationDurationHours)) return null;
      return clamp(operationDurationHours - jobOtHour, 0, operationDurationHours);
    }, [jobOtHour, operationDurationHours]);

    const isAllStartOperationInRange = React.useMemo(() => {
      if (isEmpty(selectedAttendances)) return true;
      return every(selectedAttendances, (attendance) => isAttendanceInRange(attendance, 'start'));
    }, [selectedAttendances]);
    const isAllEndOperationInRange = React.useMemo(() => {
      if (isEmpty(selectedAttendances)) return true;
      return every(selectedAttendances, (attendance) => isAttendanceInRange(attendance, 'end'));
    }, [selectedAttendances]);

    const {
      approveAttendances,
      result: { loading: approveLoading },
    } = useApproveAttendancesMutation();
    const {
      rejectAttendances,
      result: { loading: rejectLoading },
    } = useRejectAttendancesMutation();
    const {
      updateAttendances,
      result: { loading: updateLoading },
    } = useUpdateAttendancesMutation();

    const isSubmitting = approveLoading || rejectLoading || updateLoading;
    const [actionErrorMessage, setActionErrorMessage] = React.useState<string | null>(null);
    const [updateErrorMessage, setUpdateErrorMessage] = React.useState<string | null>(null);

    const [pickerKey, setPickerKey] = React.useState('');

    React.useEffect(() => {
      reset(defaultFormValues);
      // Change key to force re-mount DateTimePicker and update default values
      setPickerKey(
        `${defaultFormValues.startOperationTime}-${defaultFormValues.endOperationTime}-${defaultFormValues.breakDurationMinute}-${open}`,
      );
    }, [defaultFormValues, reset, open]); // `open` is added to the dependency array to reset the form values every time the drawer is opened

    // Sync form dirty state with batch drawer dirty flag
    React.useEffect(() => {
      setHasUnsavedChanges(isDirty);
    }, [isDirty]);

    const timeChangeHandler = React.useCallback(
      (name: 'startOperationTime' | 'endOperationTime', time: Dayjs | null) => {
        if (isNil(time)) {
          setValue(name, null, { shouldDirty: true });
          return;
        }

        const isEnd = name === 'endOperationTime';
        const targetDate = isEnd ? job?.jobEndAt : job?.jobStartAt;
        const startTime = isEnd ? getValues('startOperationTime') : undefined;

        const newTime = syncTimeToDate(time, targetDate, startTime);
        setValue(name, newTime, { shouldDirty: true });
      },
      [job?.jobEndAt, job?.jobStartAt, setValue, getValues],
    );

    const approveAttendancesHandler = React.useCallback(async () => {
      if (isEmpty(selectedAttendances)) return;
      if (!editable) return;

      setActionErrorMessage(null);

      const result = await approveAttendances(map(selectedAttendances, (attendance) => ({ id: attendance.id })));

      if (result.success) {
        snackbar.enqueueSuccess(SAVE_SUCCESS);
        forEach(selectedAttendances, (attendance) => selectAttendance(attendance.id, false));
        setOpenBatchModal(false);
      } else if (result.errors || result.error) {
        const errorMessage = result.errors ? joinPairs(result.errors) : get(result.error, 'message', 'Unknown error');
        setActionErrorMessage(errorMessage);
      }
    }, [approveAttendances, selectedAttendances]);

    const rejectAttendancesHandler = React.useCallback(async () => {
      if (isEmpty(selectedAttendances)) return;
      if (!editable) return;

      setActionErrorMessage(null);

      const result = await rejectAttendances(map(selectedAttendances, (attendance) => ({ id: attendance.id })));

      if (result.success) {
        snackbar.enqueueSuccess(SAVE_SUCCESS);
        forEach(selectedAttendances, (attendance) => selectAttendance(attendance.id, false));
        setOpenBatchModal(false);
      } else if (result.errors || result.error) {
        const errorMessage = result.errors ? joinPairs(result.errors) : get(result.error, 'message', 'Unknown error');
        setActionErrorMessage(errorMessage);
      }
    }, [rejectAttendances, selectedAttendances]);

    const editAttendancesHandler = React.useCallback(
      async (data: AttendanceEditFormTransformedValues) => {
        if (isEmpty(selectedAttendances)) return;
        if (!editable) return;

        setUpdateErrorMessage(null);

        const newAttendances = map(selectedAttendances, (attendance) => {
          const newStartOperationTime = data.startOperationTime ?? attendance.startOperationTime ?? null;
          const newEndOperationTime = data.endOperationTime ?? attendance.endOperationTime ?? null;

          return {
            id: attendance.id,
            startOperationTime: newStartOperationTime ? dayjs(newStartOperationTime).toISOString() : null,
            endOperationTime: newEndOperationTime ? dayjs(newEndOperationTime).toISOString() : null,
            breakDurationMinute: data.breakDurationMinute ?? attendance.breakDurationMinute ?? null,
          };
        });

        const result = await updateAttendances(newAttendances);

        if (result.success) {
          snackbar.enqueueSuccess(SAVE_SUCCESS);
          reset(defaultFormValues);
        } else if (result.errors || result.error) {
          const errorMessage = result.errors ? joinPairs(result.errors) : get(result.error, 'message', 'Unknown error');
          setUpdateErrorMessage(errorMessage);
        }
      },
      [selectedAttendances, updateAttendances, editable],
    );

    return (
      <Drawer
        open={open}
        onClose={onCloseHandler}
        anchor="right"
        variant="persistent"
        PaperProps={{
          variant: 'outlined',
          sx: { width: '100%', maxWidth: theme.breakpoints.values.sm },
        }}
        {...props}
      >
        <Toolbar>
          <IconButton size="large" edge="start" color="inherit" onClick={onCloseHandler}>
            <Start />
          </IconButton>
          <div style={{ flex: 1 }} />
        </Toolbar>

        {isEmpty(selectedAttendances) ? (
          <ContainerFlexHeight maxWidth="md" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CardLevel variant="outlined">
              <CardContent>{'กรุณาเลือกการเข้าออกงานก่อนทำการดำเนินการแบบกลุ่ม'}</CardContent>
            </CardLevel>
          </ContainerFlexHeight>
        ) : (
          <ContainerFlexHeight maxWidth="md" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Stack gap={1} flexDirection="row" justifyContent="flex-end" alignItems="baseline">
              {editable ? (
                <>
                  {isDirty ? (
                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                      * {attendancesDetailPageI18n('unsaved_changes')}
                    </Typography>
                  ) : null}
                  <Stack gap={0.5} alignItems="flex-end">
                    <Stack flexDirection="row" gap={1}>
                      <LoadingButton
                        variant="outlined"
                        color="error"
                        disabled={isDirty || isSubmitting}
                        loading={rejectLoading}
                        onClick={rejectAttendancesHandler}
                      >
                        {attendancesDetailPageI18n('reject_button')}
                      </LoadingButton>
                      <LoadingButton
                        variant="contained"
                        color="primary"
                        disabled={isDirty || isSubmitting}
                        loading={approveLoading}
                        onClick={approveAttendancesHandler}
                      >
                        {attendancesDetailPageI18n('approve_button')}
                      </LoadingButton>
                    </Stack>
                    {actionErrorMessage ? (
                      <Typography variant="caption" color="error">
                        {actionErrorMessage}
                      </Typography>
                    ) : null}
                  </Stack>
                </>
              ) : null}
            </Stack>

            <Stack gap={2} flexDirection="row" alignItems="center">
              <AvatarGroup max={3} total={size(selectedAttendances)}>
                {map(avatarUrls, (url, index) => (
                  <Avatar key={url ?? index} src={url ?? ''} sx={{ width: 80, height: 80 }}>
                    <Person />
                  </Avatar>
                ))}
              </AvatarGroup>
            </Stack>

            <form onSubmit={handleSubmit(editAttendancesHandler)}>
              <Stack gap={3}>
                <Stack gap={1}>
                  <Controller
                    key="startOperationTime"
                    control={control}
                    name="startOperationTime"
                    render={({ field: { value, ...field }, fieldState: { error, invalid } }) => (
                      <Stack flexDirection="row" gap={1} alignItems="baseline">
                        <Typography variant="body1">{attendancesDetailPageI18n('clock_in_time_label')}: </Typography>

                        <Stack flexDirection="row" gap={1} alignItems="baseline" flexWrap="wrap">
                          <TimePickerWithShorthand
                            key={pickerKey}
                            {...field}
                            readOnly={!editable}
                            value={value ? dayjs(value) : null}
                            onChange={(time) => timeChangeHandler('startOperationTime', time)}
                            desktopModeMediaQuery="@media (max-width: 1px)"
                            slotProps={{
                              textField: {
                                error: invalid,
                                helperText: error?.message,
                                InputProps: {
                                  size: 'small',
                                  endAdornment: <Schedule />,
                                  sx: { maxWidth: 160, textAlign: 'left' },
                                },
                              },
                            }}
                          />

                          {isAllStartOperationInRange ? null : (
                            <Typography variant="caption" color="error">
                              {attendancesDetailPageI18n('clock_in_location_some_out_of_range')}
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    )}
                  />
                </Stack>

                <Stack gap={1}>
                  <Controller
                    key="endOperationTime"
                    control={control}
                    name="endOperationTime"
                    render={({ field: { value, ...field }, fieldState: { error, invalid } }) => (
                      <Stack flexDirection="row" gap={1} alignItems="baseline">
                        <Typography variant="body1">{attendancesDetailPageI18n('clock_out_time_label')}: </Typography>

                        <Stack flexDirection="row" gap={1} alignItems="baseline" flexWrap="wrap">
                          <TimePickerWithShorthand
                            key={pickerKey}
                            {...field}
                            readOnly={!editable}
                            value={value ? dayjs(value) : null}
                            onChange={(time) => timeChangeHandler('endOperationTime', time)}
                            desktopModeMediaQuery="@media (max-width: 1px)"
                            slotProps={{
                              textField: {
                                error: invalid,
                                helperText: error?.message,
                                InputProps: {
                                  size: 'small',
                                  endAdornment: <Schedule />,
                                  sx: { maxWidth: 160, textAlign: 'left' },
                                },
                              },
                            }}
                          />

                          {isAllEndOperationInRange ? null : (
                            <Typography variant="caption" color="error">
                              {attendancesDetailPageI18n('clock_out_location_some_out_of_range')}
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    )}
                  />
                </Stack>

                <Controller
                  key="breakDurationMinute"
                  control={control}
                  name="breakDurationMinute"
                  render={({ field: { value, onChange, ...field }, fieldState: { error, invalid } }) => (
                    <Stack gap={1} flexDirection="row" alignItems="baseline">
                      <Typography variant="body1">{attendancesDetailPageI18n('break_duration_label')}: </Typography>
                      <NumberTextField
                        {...field}
                        size="small"
                        value={value ?? ''}
                        onNumberChange={onChange}
                        placeholder={attendancesDetailPageI18n('break_duration_placeholder')}
                        error={invalid}
                        helperText={error?.message}
                        InputProps={{
                          readOnly: !editable,
                          fullWidth: true,
                          inputProps: { pattern: '[0-9]*' },
                          endAdornment: (
                            <InputAdornment position="end">{attendancesDetailPageI18n('minutes_unit')}</InputAdornment>
                          ),
                          sx: { maxWidth: 160, textAlign: 'left' },
                        }}
                      />
                    </Stack>
                  )}
                />

                <Stack gap={1}>
                  <Typography variant="body1">{attendancesDetailPageI18n('time_summary_label')}:</Typography>
                  <Stack gap={0.5} paddingLeft={2}>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {attendancesDetailPageI18n('job_group_label')}
                    </Typography>
                    <Typography variant="body2">
                      {attendancesDetailPageI18n('job_start_at_label')}:{' '}
                      {job?.jobStartAt ? formatTime(job.jobStartAt) : '-'}
                    </Typography>
                    <Typography variant="body2">
                      {attendancesDetailPageI18n('job_end_at_label')}: {job?.jobEndAt ? formatTime(job.jobEndAt) : '-'}
                    </Typography>
                    <Typography variant="body2">
                      {attendancesDetailPageI18n('job_break_duration_label')}:{' '}
                      {isNil(job?.breakDurationMinute)
                        ? '-'
                        : attendancesDetailPageI18n('job_break_duration_value', { count: job?.breakDurationMinute })}
                    </Typography>
                    <Typography variant="body2">
                      {attendancesDetailPageI18n('job_duration_label')}:{' '}
                      {isNil(jobDurationHours)
                        ? '-'
                        : attendancesDetailPageI18n('job_duration_value', { count: jobDurationHours })}
                    </Typography>
                    <Typography variant="body2">
                      {attendancesDetailPageI18n('ot_threshold_label')}:{' '}
                      {isNil(jobOtHour) ? '-' : attendancesDetailPageI18n('ot_threshold_value', { count: jobOtHour })}
                    </Typography>
                  </Stack>
                  <Stack gap={0.5} paddingLeft={2}>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {attendancesDetailPageI18n('attendance_group_label')}
                    </Typography>
                    <Typography variant="body2">
                      {attendancesDetailPageI18n('operation_duration_label')}:{' '}
                      {isNil(operationDurationHours)
                        ? '-'
                        : attendancesDetailPageI18n('operation_duration_value', { count: operationDurationHours })}
                    </Typography>
                    <Typography variant="body2">
                      {attendancesDetailPageI18n('ot_duration_label')}:{' '}
                      {isNil(operationDurationOtHours)
                        ? '-'
                        : attendancesDetailPageI18n('ot_duration_value', {
                            count: operationDurationOtHours,
                            threshold: jobOtHour,
                          })}
                    </Typography>
                  </Stack>
                </Stack>

                <Stack paddingTop={1} paddingBottom={2} alignItems="flex-end">
                  {updateErrorMessage ? (
                    <Typography variant="body2" color="error">
                      {updateErrorMessage}
                    </Typography>
                  ) : null}
                  {editable ? (
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={!isDirty || isSubmitting}
                      loading={updateLoading}
                    >
                      {attendancesDetailPageI18n('save_changes_button')}
                    </LoadingButton>
                  ) : null}
                </Stack>
              </Stack>
            </form>
          </ContainerFlexHeight>
        )}
      </Drawer>
    );
  },
);

const BatchDrawerContainer = ({ open, ...rest }: BatchDrawerProps) => {
  const { openBatchModal, setOpenBatchModal, selectedAttendances, selectAttendance, setBatchDrawerDirty } =
    React.useContext(AttendancesPageContext);

  React.useEffect(() => {
    // Auto close batch drawer when selectedAttendances is empty
    if (isEmpty(selectedAttendances)) {
      setOpenBatchModal(false);
      setBatchDrawerDirty(false);
    }
  }, [setOpenBatchModal, selectedAttendances, setBatchDrawerDirty]);

  // Clear dirty flag when drawer closes
  React.useEffect(() => {
    if (!openBatchModal) {
      setBatchDrawerDirty(false);
    }
  }, [openBatchModal, setBatchDrawerDirty]);

  return (
    <BatchDrawer
      open={open ?? openBatchModal}
      setOpenBatchModal={setOpenBatchModal}
      selectedAttendances={selectedAttendances}
      selectAttendance={selectAttendance}
      setHasUnsavedChanges={setBatchDrawerDirty}
      {...rest}
    />
  );
};

export default BatchDrawerContainer;
